import * as React from 'react'
import { useSearchParams } from 'react-router-dom'

import CheckoutSummary from '../components/Paywall/CheckoutSummary'
import CheckoutForm from '../components/Paywall/CheckoutForm'
import CheckoutSuccess from '../components/Paywall/CheckoutSuccess'
import UpgradeHeader, {
    CheckoutLoadFailed,
    CheckoutUnavailable,
} from '../components/Paywall/checkout/UpgradeHeader'
import { CARD_FIELD_IDS } from '../components/Paywall/checkout/CardFields'
import {
    useBillingConfig,
    useCardComponent,
} from '../components/Paywall/useChargebeeCheckout'
import { useWalletPayment } from '../components/Paywall/useWalletPayment'
import { useCheckoutQuote } from '../components/Paywall/useCheckoutQuote'
import { usePlanSelection } from '../components/Paywall/usePlanSelection'
import { useUpgradeFunnel } from '../components/Paywall/useUpgradeFunnel'
import { useCouponField } from '../components/Paywall/useCouponField'
import { useVatField } from '../components/Paywall/useVatField'
import { usePlanChangePreview } from '../components/Paywall/usePlanChangePreview'
import { usePlanChange } from '../components/Paywall/usePlanChange'
import { usePaymentIntents } from '../components/Paywall/usePaymentIntents'
import { usePurchase } from '../components/Paywall/usePurchase'
import { refusalMessage } from '../components/Paywall/checkoutErrors'
import {
    canPayFor,
    payLabelFor,
    recurringNoteFor,
} from '../components/Paywall/checkoutLabels'
import PricingComparisonModal from '../components/PricingComparisonModal'
import { appProductName, getVariantConfig } from '../appVariant'

const EMPTY_DETAILS = { country: '', company: '', vatNumber: '', coupon: '' }

function safeReturnPath(value) {
    return typeof value === 'string' && /^\/(?![/\\])/.test(value)
        ? value
        : '/apps'
}

// /upgrade: plan, billing details and card on one screen, the running total on
// the left. A visitor who already pays gets the plan-change mode instead.
export default function Upgrade() {
    const [searchParams] = useSearchParams()
    const returnTo = safeReturnPath(searchParams.get('return'))
    const reasonCode = searchParams.get('reason')

    const billing = useBillingConfig(true)
    const config = billing.config
    const currentPlanId = (config && config.currentPlanId) || null
    const mode = currentPlanId ? 'change' : 'buy'
    const buying = mode === 'buy'
    // Without the subscription there is no telling buy from change, and a
    // subscriber offered a purchase is refused with ALREADY_SUBSCRIBED on Pay.
    const subscriptionKnown =
        billing.status === 'ready' && config.subscriptionLookup !== 'failed'

    // No country is guessed: the one chosen here is what every later invoice
    // is taxed from, and a browser's language says nothing about residence.
    const [details, setDetails] = React.useState(EMPTY_DETAILS)
    const [done, setDone] = React.useState(null)
    const [compareOpen, setCompareOpen] = React.useState(false)
    // The card and wallet containers exist only while the form is on screen.
    const formShown = subscriptionKnown && !done

    const selection = usePlanSelection({
        requestedPlan: searchParams.get('plan'),
        requestedPeriod: searchParams.get('period'),
        currentPlanId,
        planPrices: (config && config.planPrices) || null,
    })
    const { period, selectedPlan } = selection

    const quote = useCheckoutQuote({
        planId: selectedPlan && selectedPlan.planId,
        // A plan change renews at the price for the country and VAT number on
        // file; only a first purchase prices the details being entered.
        country: buying ? details.country : config && config.billingCountry,
        vatNumber: buying
            ? details.vatNumber
            : config && config.billingVatNumber,
        coupon: details.coupon,
        enabled: Boolean(config) && !done,
    })
    const funnel = useUpgradeFunnel({
        reasonCode,
        mode,
        selectedPlan,
        period,
        billingSettled: billing.status !== 'idle' && billing.status !== 'loading',
    })
    const coupon = useCouponField({ selectedPlan, details, setDetails, quote })
    const vat = useVatField({ config, details, setDetails })
    const preview = usePlanChangePreview({
        enabled: mode === 'change' && !done,
        selectedPlan,
        currentPlanId,
    })
    const planChange = usePlanChange({
        selectedKey: selection.selectedKey,
        selectedPlan,
        period,
        changePreview: preview.preview,
        funnel,
        onDone: setDone,
    })

    const intents = usePaymentIntents({ selectedPlan, details })
    const card = useCardComponent({
        enabled: formShown && buying,
        site: config && config.site,
        publishableKey: config && config.publishableKey,
        fieldIds: CARD_FIELD_IDS,
        onComplete: intents.requestIntent,
    })
    const purchase = usePurchase({
        selectedPlan,
        details,
        period,
        config,
        card,
        intents,
        funnel,
        onDone: setDone,
        onVatRefused: vat.refuse,
    })
    const walletAmountKey = quote.quote
        ? `${intents.intentKey}|${quote.quote.amount}`
        : null
    const wallets = useWalletPayment({
        enabled:
            formShown &&
            buying &&
            Boolean(details.country) &&
            Boolean(walletAmountKey) &&
            !vat.blocksPayment,
        site: config && config.site,
        publishableKey: config && config.publishableKey,
        amountKey: walletAmountKey,
        requestIntent: intents.requestIntent,
        onStarted: purchase.walletStarted,
        onCancelled: purchase.walletCancelled,
        onAuthorized: purchase.payWithWallet,
        onError: purchase.payFailed,
    })

    function selectPlan(key) {
        if (key === selection.selectedKey) return
        selection.setSelectedKey(key)
        const option = selection.options.find(candidate => candidate.key === key)
        funnel.track('upgrade_plan_selected', { plan: option && option.planId })
    }

    function changePeriod(nextPeriod) {
        if (nextPeriod === period) return
        selection.setPeriod(nextPeriod)
        const option = selection.optionFor(selection.selectedKey, nextPeriod)
        funnel.track('upgrade_period_changed', {
            plan: option && option.planId,
            period: nextPeriod,
        })
    }

    const quoteFailure =
        quote.status === 'failed' && !coupon.error
            ? refusalMessage(
                  quote.error,
                  'Could not load the price for this plan. Please try again.'
              )
            : null
    const actionError = buying
        ? purchase.error
        : planChange.error || preview.error
    const payment = {
        label: payLabelFor({
            mode,
            selectedPlan,
            changePreview: preview.preview,
            pendingCompletion: purchase.pendingCompletion,
            quote: quote.quote,
            country: details.country,
        }),
        canPay: canPayFor({
            mode,
            selectedPlan,
            currentPlanId,
            changePreview: preview.preview,
            previewStatus: preview.status,
            agreed: planChange.agreed,
            pendingCompletion: purchase.pendingCompletion,
            country: details.country,
            quote,
            card,
            vatBlocksPayment: vat.blocksPayment,
        }),
        status: buying ? purchase.status : planChange.status,
        error: actionError || quoteFailure,
        attempts: purchase.attempts,
        finishing: Boolean(purchase.pendingCompletion),
        recurringNote: recurringNoteFor({
            quote: details.country ? quote.quote : null,
            period,
        }),
        onPay: purchase.payByCard,
        onConfirmChange: planChange.confirm,
    }

    const productName = appProductName()

    if (billing.status === 'loading' || billing.status === 'idle') {
        return (
            <PageShell>
                <p className="tw-text-sm tw-text-muted-foreground">
                    Loading your plans…
                </p>
            </PageShell>
        )
    }

    if (!subscriptionKnown) {
        return (
            <PageShell>
                {billing.status === 'disabled' ? (
                    <CheckoutUnavailable productName={productName} />
                ) : (
                    <CheckoutLoadFailed productName={productName} />
                )}
            </PageShell>
        )
    }

    if (done) {
        return (
            <PageShell>
                <CheckoutSuccess
                    planLabel={done.label}
                    mode={done.mode}
                    amount={done.amount}
                    currency={done.currency}
                    scheduledFor={done.scheduledFor}
                    trialEndsAt={done.trialEndsAt}
                    returnTo={returnTo}
                    site={config.site}
                />
            </PageShell>
        )
    }

    return (
        <PageShell>
            <UpgradeHeader
                returnTo={returnTo}
                mode={mode}
                productName={productName}
                reasonCode={reasonCode}
            />

            <div className="tw-grid tw-grid-cols-1 tw-gap-8 lg:tw-grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:tw-gap-12">
                <CheckoutSummary
                    options={selection.options}
                    selectedKey={selection.selectedKey}
                    onSelectPlan={selectPlan}
                    period={period}
                    onPeriodChange={changePeriod}
                    saving={selection.saving}
                    currentPlanId={currentPlanId}
                    quote={quote.quote}
                    loading={quote.status === 'loading'}
                    mode={mode}
                    country={details.country}
                    onCompare={() => setCompareOpen(true)}
                />
                <div className="tw-rounded-xl tw-border tw-border-solid tw-border-border tw-bg-card tw-p-5 sm:tw-p-6">
                    <CheckoutForm
                        mode={mode}
                        email={config.email}
                        billing={config}
                        details={details}
                        onChangeDetails={setDetails}
                        coupon={coupon}
                        vat={vat}
                        card={card}
                        wallets={wallets}
                        change={{
                            preview: preview.preview,
                            previewStatus: preview.status,
                            planLabel: selectedPlan && selectedPlan.label,
                            renewalAmount: quote.quote && quote.quote.amount,
                            renewalCurrency: quote.quote && quote.quote.currency,
                            period,
                            billingPortalUrl: getVariantConfig().brand
                                .billingPortalUrl,
                            billingCountry: config.billingCountry || null,
                            agreed: planChange.agreed,
                            onAgreedChange: planChange.setAgreed,
                            onPaymentMethodAdded: preview.reprice,
                        }}
                        payment={payment}
                    />
                </div>
            </div>

            <PricingComparisonModal
                open={compareOpen}
                onClose={() => setCompareOpen(false)}
            />
        </PageShell>
    )
}

function PageShell({ children }) {
    return (
        // min-height fills the viewport so the theme background (painted by the
        // ScopedCssBaseline above this) covers the page, not just the content.
        <div className="project-notes-scope" style={{ minHeight: '100vh' }}>
            <div
                className="tw-mx-auto tw-w-full tw-max-w-5xl tw-px-4 tw-text-foreground sm:tw-px-6"
                style={{ paddingBlock: '32px' }}
                data-testid="upgrade-page"
            >
                {children}
            </div>
        </div>
    )
}
